import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';

import { Icon as DropdownIcon } from '@components';
import { DASHBOARD } from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import { MixpanelEventV2, sendMixpanelEventV2 } from '@core/analytics/mixpanel';
import ApiMethodChip from '@features/docs/components/api-method-chip/ApiMethodChip';
import { getLocalStorageItem } from '@utils/storage';

import { Item, SidebarItemProps } from './types';

const SidebarItem = (props: SidebarItemProps) => {
  const location = useLocation();

  const { item, path, isOrgLevelItem, branchId, isProfileList, isNestedItem } =
    props;

  const { id, Icon, label, route, children, method } = item;

  // Helper to check if current route matches this item or any of its descendants
  const isRouteActive = (sidebarItem: Item, currentPath: string): boolean => {
    if (currentPath.startsWith(sidebarItem.route)) {
      return true;
    }
    if (sidebarItem.children && sidebarItem.children.length > 0) {
      return sidebarItem.children.some((child) =>
        isRouteActive(child, currentPath)
      );
    }
    // to handle additional child routes like details, edit, etc.
    if (sidebarItem.additionalChildRoutes) {
      return sidebarItem.additionalChildRoutes.some((childRoute: string) =>
        currentPath.startsWith(childRoute.split(':')[0])
      );
    }
    return false;
  };

  // Use the recursive check for current route
  const isCurrentRoute = isRouteActive(item, location.pathname + location.hash);

  const [isExpanded, setIsExpanded] = useState(isCurrentRoute);

  const itemBgColor = isCurrentRoute
    ? 'bg-cofeeGreyBlue75'
    : 'hover:bg-cofeeGreyBlue75';
  // eslint-disable-next-line no-nested-ternary
  const itemIconColor = isCurrentRoute
    ? 'text-cofeePrimary6'
    : isProfileList
    ? 'text-cofeeNeutral8'
    : 'text-cofeeNeutral10';
  const itemTextColor = 'text-cofeeNeutral10';

  const sideBarClick = () => {
    // sendMixpanelEvent(Event.SIDEBAR_TAB_CLICKED, {
    //   section: label,
    //   branchId
    // });
    const targetRoute = path || route;
    if (targetRoute === DASHBOARD) {
      sendMixpanelEventV2(MixpanelEventV2.BRANCH_DASHBOARD_CLICKED, {
        branch_name: getLocalStorageItem(STORAGE_KEYS.BRANCH_NAME)
      });
    }

    if (children) {
      setIsExpanded(!isExpanded);
    }
  };

  const renderNestedList = (child: Item) => {
    const { id: childId, route: childRoute } = child;

    return (
      <SidebarItem
        key={childId}
        item={child}
        path={childRoute}
        isOrgLevelItem
        branchId={branchId}
        isProfileList={isProfileList}
        isNestedItem
      />
    );
  };

  return (
    <div className="relative ml-2 mr-1">
      <Link
        key={id}
        to={path || route}
        className={
          isOrgLevelItem
            ? `flex items-center justify-between hover:!bg-cofeeGreyBlue75 ${
                isProfileList && isCurrentRoute && !isNestedItem
                  ? 'mt-0 bg-cofeeGreyBlue75'
                  : ''
              }  ${
                isProfileList && isCurrentRoute
                  ? 'rounded-lg bg-cofeeGreyBlue75 hover:bg-cofeeGreyBlue75'
                  : ''
              }`
            : `flex items-center gap-3 rounded-l-lg px-4 py-3 ${itemBgColor}`
        }
        onClick={sideBarClick}
      >
        <div
          className={`flex items-center gap-3 ${
            isProfileList ? 'py-2.5 pl-3' : ''
          } ${isNestedItem ? '!py-2 !pl-2' : ''}`}
        >
          {Icon && <Icon className={clsx('size-5', itemIconColor)} />}
          {method && <ApiMethodChip method={method} />}
          <span
            className={`break-words text-sm font-medium ${
              isProfileList && !isNestedItem ? ' text-darkGrey' : itemTextColor
            }`}
          >
            {label}
          </span>
        </div>
        {(children?.length ?? 0) > 0 && (
          <span className="mr-3 text-bgGrey">
            <DropdownIcon
              name="down-icon-dark"
              size="16"
              className={clsx(
                'transition-transform duration-300 ease-in-out',
                isExpanded
                  ? 'rotate-180 transition-transform duration-300'
                  : 'rotate-0 transition-transform duration-300'
              )}
            />
          </span>
        )}
      </Link>
      {isCurrentRoute && !isNestedItem && (
        <div
          className={`absolute -right-1 top-0 w-1 rounded-l-lg bg-cofeePrimary6 ${
            isProfileList ? 'h-10' : 'h-11'
          }`}
        />
      )}
      <div
        className={clsx(
          'my-0.5 ml-4.5 flex flex-col overflow-hidden transition-[max-height,opacity] duration-300 ease-[cubic-bezier(0.98,0.16,0.2,1)]',
          isExpanded ? 'max-h-96 opacity-100' : 'pointer-events-none max-h-0'
        )}
      >
        {children?.length &&
          children.length > 0 &&
          children.map((child) => renderNestedList(child))}
      </div>
    </div>
  );
};

export default SidebarItem;
